import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import "moment/locale/es";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Col, Label, Row } from "reactstrap";
import { INSTAGRAM_INSIGHT_LOADING } from "../../constants/loading.constant";
import { Errors } from "../../constants/errors";
import { useAnalyticsCore } from "../../cores/analytics";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { InstagramAnalyticsDateRangeFilter } from "../component/analytics/InstagramAnalyticsDateRangeFilter";
import { InstagramAnalyticsDashboard } from "../component/analytics/InstagramAnalyticsDashboard";
import { buildInstagramAnalyticsSnapshotFromApi } from "../component/analytics/instagramAnalyticsFromInsights";
import { instagramAnalyticsMock } from "../component/analytics/instagramAnalyticsMock";
import { PanelWembii } from "../component/PanelWembii";
import { InstagramIcon } from "../component/social/Instagram/InstagramIcon";
import { ErrorIcon } from "../component/socialPost/ErrorIcon";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import { analyticsApiService } from "../../cores/analytics/services/api";

const formatInstagramPeriodLabel = (since: string, until: string) => {
  const fmt = (iso: string) => {
    const m = moment(iso).locale("es");
    const month = m.format("MMM").replace(/\./g, "").toLowerCase();
    return `${m.date()} ${month}. ${m.year()}`;
  };
  return `${fmt(since)} - ${fmt(until)}`;
};

export const InstagramAnalytics = () => {
  const intl = useIntl();
  const {
    date,
    getInstagramInsights,
    getInstagramFeedPosts,
    instagramInsights,
    instagramFeedPost,
    instagramErrorCode,
  } = useAnalyticsCore();
  const { isLoading } = useGlobalCore();
  const {
    company,
    socialSettings,
    companyPlan,
    companyRole,
    isAdmin,
    isReseller,
  } = useMeCore();
  const { goToSocial } = useRouterCore();
  const [remoteRanking, setRemoteRanking] = useState<any[]>([]);
  const [remoteHashtags, setRemoteHashtags] = useState<any[]>([]);
  const [previousInsightsEntities, setPreviousInsightsEntities] = useState<Record<string, any>>({});
  const [previousFeedPosts, setPreviousFeedPosts] = useState<any[]>([]);
  const [previousSince, setPreviousSince] = useState<string>("");
  const [previousUntil, setPreviousUntil] = useState<string>("");
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [loadingHashtags, setLoadingHashtags] = useState(false);

  const isSync = useMemo(
    () => Boolean(socialSettings && socialSettings.instagram),
    [socialSettings]
  );

  const isFutureDate = (d: string) => moment(d).isAfter(moment());

  const areDatesInverted = (since: string, until: string) =>
    moment(since).isAfter(moment(until));

  useEffect(() => {
    if (
      !isSync ||
      !companyPlan?.rules.allowAnalytics ||
      (!companyRole?.analitycs &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [goToSocial, isSync, companyPlan, companyRole, isAdmin, isReseller]);

  useEffect(() => {
    if (isSync && company?.id) void getInstagramFeedPosts(company.id);
  }, [company?.id, isSync, date.since, date.until, getInstagramFeedPosts]);

  useEffect(() => {
    if (
      !isSync ||
      !company?.id ||
      isFutureDate(date.since) ||
      isFutureDate(date.until) ||
      areDatesInverted(date.since, date.until)
    ) {
      return;
    }
    const sinceIso = moment(date.since, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true).isValid()
      ? moment(date.since, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true).format("YYYY-MM-DD")
      : date.since;
    const untilIso = moment(date.until, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true).isValid()
      ? moment(date.until, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true).format("YYYY-MM-DD")
      : date.until;

    setLoadingRanking(true);
    analyticsApiService
      .instagramPublications(company.id, sinceIso, untilIso, "ranking")
      .then((payload) => setRemoteRanking(payload?.contentRanking || []))
      .catch((error) => {
        console.error(error);
        setRemoteRanking([]);
      })
      .finally(() => setLoadingRanking(false));

    setLoadingHashtags(true);
    analyticsApiService
      .instagramPublications(company.id, sinceIso, untilIso, "hashtags")
      .then((payload) => setRemoteHashtags(payload?.hashtags || []))
      .catch((error) => {
        console.error(error);
        setRemoteHashtags([]);
      })
      .finally(() => setLoadingHashtags(false));
  }, [company?.id, date.since, date.until, isSync]);

  useEffect(() => {
    if (
      !isSync ||
      !company?.id ||
      isFutureDate(date.since) ||
      isFutureDate(date.until) ||
      areDatesInverted(date.since, date.until)
    ) {
      return;
    }
    const sinceM = moment(date.since, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
    const untilM = moment(date.until, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
    if (!sinceM.isValid() || !untilM.isValid()) return;
    const days = Math.max(1, untilM.diff(sinceM, "days") + 1);
    const prevUntil = sinceM.clone().subtract(1, "day").endOf("day");
    const prevSince = prevUntil.clone().subtract(days - 1, "days").startOf("day");
    const prevSinceIso = prevSince.format("YYYY-MM-DD");
    const prevUntilIso = prevUntil.format("YYYY-MM-DD");
    setPreviousSince(prevSinceIso);
    setPreviousUntil(prevUntilIso);

    analyticsApiService
      .instagramInsights(company.id, prevSinceIso, prevUntilIso, "Daily")
      .then((metrics) => {
        const entities = (metrics || []).reduce((acc: Record<string, any>, m: any) => {
          if (m?.name) acc[m.name] = m;
          return acc;
        }, {});
        setPreviousInsightsEntities(entities);
      })
      .catch((error) => {
        console.error(error);
        setPreviousInsightsEntities({});
      });

    analyticsApiService
      .instagramPublications(company.id, prevSinceIso, prevUntilIso, "all")
      .then((payload) => setPreviousFeedPosts(payload?.post || []))
      .catch((error) => {
        console.error(error);
        setPreviousFeedPosts([]);
      });
  }, [company?.id, date.since, date.until, isSync]);

  useEffect(() => {
    if (
      isSync &&
      company?.id &&
      !isFutureDate(date.since) &&
      !isFutureDate(date.until) &&
      !areDatesInverted(date.since, date.until)
    )
      void getInstagramInsights(company.id, date.since, date.until);
  }, [company?.id, date.since, date.until, isSync, getInstagramInsights]);

  const igSetting = socialSettings?.instagram;

  const accountName = useMemo(() => {
    const raw = igSetting?.name?.trim();
    if (!raw) return instagramAnalyticsMock.accountName;
    return raw.startsWith("@") ? raw : `@${raw}`;
  }, [igSetting?.name]);

  const communityName = useMemo(
    () =>
      company?.name ??
      igSetting?.name?.replace(/^@/, "") ??
      instagramAnalyticsMock.communityName,
    [company?.name, igSetting?.name]
  );

  const avatarUrl = igSetting?.avatarUrl ?? igSetting?.avatar;

  const periodLabel = useMemo(
    () => formatInstagramPeriodLabel(date.since, date.until),
    [date.since, date.until]
  );

  const snapshot = useMemo(() => {
    const updatedAt = moment().toISOString();
    const base = buildInstagramAnalyticsSnapshotFromApi({
      insightsEntities: instagramInsights,
      feedPosts: instagramFeedPost,
      previousInsightsEntities,
      previousFeedPosts,
      accountName,
      communityName,
      periodLabel,
      updatedAt,
      since: date.since,
      until: date.until,
      previousSince,
      previousUntil,
    });
    return {
      ...base,
      topContent: remoteRanking.length ? remoteRanking : base.topContent,
      hashtags: remoteHashtags.length ? remoteHashtags : base.hashtags,
    };
  }, [
    accountName,
    communityName,
    periodLabel,
    date.since,
    date.until,
    instagramFeedPost,
    instagramInsights,
    previousInsightsEntities,
    previousFeedPosts,
    previousSince,
    previousUntil,
    remoteRanking,
    remoteHashtags,
  ]);

  const instagramPermissionError =
    instagramErrorCode === Errors.PermissionsMissing ? (
      <Label className="text-muted text-center mb-0 w-100">
        {intl.formatMessage({ id: "app.social.permissionsError" })}.
      </Label>
    ) : null;

  return (
    <Layout>
      {isLoading(INSTAGRAM_INSIGHT_LOADING) ? (
        <UpdateLoadingCard
          loadingId={INSTAGRAM_INSIGHT_LOADING}
          text={intl.formatMessage({ id: "app.reports.updating" })}
        />
      ) : null}
      <Content>
        <div className="instagram-analytics-page-header instagram-analytics-page-header--analytics">
          <div className="instagram-analytics-page-header__toolbar">
            <div className="instagram-analytics-page-header__lead">
              <h1 className="instagram-analytics-page-header__title">
                Estadísticas de Instagram
              </h1>
              <div
                className="instagram-analytics-page-header__logo instagram-analytics-page-header__logo--subtitle"
                aria-hidden
              >
                <FontAwesomeIcon icon={["fab", "instagram"]} />
              </div>
            </div>
            <div className="instagram-analytics-page-header__right">
              <InstagramAnalyticsDateRangeFilter />
              <div className="instagram-analytics-page-header__account">
                <div className="instagram-analytics-page-header__avatar-wrap">
                  {avatarUrl ? (
                    <img
                      className="instagram-analytics-page-header__avatar instagram-analytics-page-header__avatar-img"
                      src={avatarUrl}
                      alt=""
                    />
                  ) : (
                    <div
                      className="instagram-analytics-page-header__avatar"
                      aria-hidden
                    />
                  )}
                  <span className="instagram-analytics-page-header__instagram-badge">
                    <InstagramIcon active size="sm" />
                  </span>
                </div>
                <span className="instagram-analytics-page-header__company-name">
                  {communityName}
                </span>
              </div>
            </div>
          </div>
        </div>
        {instagramErrorCode ? (
          <Row className="d-flex justify-content-center mb-3">
            <Col xs="12" md="10" lg="8">
              <PanelWembii className="mb-2">
                <div className="d-flex justify-content-center align-items-center py-3">
                  <ErrorIcon size="2x" className="m-2 ml-4" />
                  {instagramPermissionError ?? (
                    <Label className="text-muted text-center mb-0 w-100">
                      {intl.formatMessage({ id: "app.toast.error4" })}
                    </Label>
                  )}
                </div>
              </PanelWembii>
            </Col>
          </Row>
        ) : null}
        <InstagramAnalyticsDashboard
          snapshot={snapshot}
          publicationsLoading={{ ranking: loadingRanking, hashtags: loadingHashtags }}
        />
      </Content>
    </Layout>
  );
};
